import { z } from 'zod'
import { Role } from '../enums'
import { NewPassword } from '../password'
import { Id } from './primitives'

export const Membership = z.object({ tenantId: Id, tenantName: z.string().min(1), role: Role })
export type Membership = z.infer<typeof Membership>

export const MeUser = z.object({ id: Id, email: z.email().nullable(), displayName: z.string() })
export type MeUser = z.infer<typeof MeUser>

export const MeResponse = z.object({
  user: MeUser,
  memberships: z.array(Membership),
  passwordChangeRequired: z.boolean(),
  multiTenantEnabled: z.boolean(),
})
export type MeResponse = z.infer<typeof MeResponse>

export const INVITE_EMAIL_MAX_CHARS = 320
export const MEMBER_DISPLAY_NAME_MAX_CHARS = 200

export const Member = z.object({
  userId: Id,
  displayName: z.string().nullable(),
  email: z.email(),
  role: Role,
})
export type Member = z.infer<typeof Member>

export const MemberList = z.object({ members: z.array(Member) })
export type MemberList = z.infer<typeof MemberList>

export const InviteMemberRequest = z.strictObject({
  email: z.email().max(INVITE_EMAIL_MAX_CHARS),
  displayName: z.string().min(1).max(MEMBER_DISPLAY_NAME_MAX_CHARS).optional(),
  role: Role,
  password: NewPassword.optional(),
})
export type InviteMemberRequest = z.infer<typeof InviteMemberRequest>

export const UpdateMemberRoleRequest = z.strictObject({ role: Role })
export type UpdateMemberRoleRequest = z.infer<typeof UpdateMemberRoleRequest>

export const RemoveMemberResponse = z.object({ userId: Id, removed: z.literal(true) })
export type RemoveMemberResponse = z.infer<typeof RemoveMemberResponse>

export const LAST_ADMIN_REFUSAL_MESSAGE = '工作區至少要保留一位管理員。請先把另一位成員設為管理員，再移除或降級這位管理員。'

export type MemberRoleChange = { userId: string; role: Role | null }

export const leavesNoAdmin = (
  members: readonly Pick<Member, 'userId' | 'role'>[],
  change: MemberRoleChange,
): boolean => {
  const target = members.find((member) => member.userId === change.userId)
  if (target?.role !== 'admin' || change.role === 'admin') return false
  return !members.some((member) => member.role === 'admin' && member.userId !== change.userId)
}
